import BulkAdjustType = ToroSaEnums.BulkAdjustType;
import SaZoneType = ToroSaEnums.SaZoneType;

import { ApiService, HttpMethod } from '../_common/api.service';
import { AppliedSaTargetProfile, SaTargetProfile } from './models/sa-target-profile.model';
import { SaData, SaUpdateTargetVwcResponse } from './models/sa-data.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SaArea } from './models/spatial-adjust-course.model';
import { SaEntityVwcHistory } from './models/sa-entity-vwc-history.model';
import { SaQueuedOperation } from './models/sa-background-operation.model';
import { SaSuggestedPercentAdjust } from './models/sa-suggested-percent-adjust.model';
import { SaUpdateZoneChartSelection } from './models/sa-update-zone-chart-selection.model';
import { SaVwcHistoryRequest } from './models/sa-vwc-history-request.model';
import { SiteManagerService } from '../site/site-manager.service';
import { SoilFactorUpdate } from '../../spatial-adjust/components/sa-soil-factor-dlg/models/soil-factor-update';
import { SpatialAdjustCourse } from './models/sa-course.model';
import { SpatialAdjustSettings } from '../site/models/spatial-adjust-settings.model';
import { ToroSaEnums } from '../../spatial-adjust/enums/spatial-adjust.enums';

@Injectable({
    providedIn: 'root'
})
export class SpatialAdjustApiService {

    private apiService: ApiService;

    // private dataUrl = '../../assets/spatial-adjust-data/missionhills_latlongs.json';

    // =========================================================================================================================================================
    // Ctor
    // =========================================================================================================================================================

    constructor(http: HttpClient, private siteManager: SiteManagerService) {
        this.apiService = new ApiService(http);
    }

    // =========================================================================================================================================================
    // Public Methods
    // =========================================================================================================================================================

    getDataFromTurfRad(orgId: number, fromDate: string, page?: number): Observable<any> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getSprinklerStatsUrl(orgId, fromDate, page), HttpMethod.Get)
            .pipe(map(result => new SaData(result)));
    }

    getCourses(orgId: number): Observable<SpatialAdjustCourse[]> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getCoursesUrl(orgId), HttpMethod.Get)
            .pipe(map((result: SpatialAdjustCourse[]) => result.map(c => new SpatialAdjustCourse(c))));
    }

    getData(orgId: number, turfRadCourseId: number = null): Observable<SaArea[]> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getDataUrl(orgId, turfRadCourseId), HttpMethod.Get)
            .pipe(map((result: SaArea[]) => result.map(a => new SaArea(a, this.siteManager))));
    }

    getTodayEt(orgId: number): Observable<number> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getTodayEtUrl(orgId), HttpMethod.Get)
    }

    getVwcHistory(vwcHistoryRequest: SaVwcHistoryRequest): Observable<SaEntityVwcHistory[]> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getVwcHistoryUrl, HttpMethod.Post, vwcHistoryRequest)
            .pipe(map((result: SaEntityVwcHistory[]) => result.map(h => new SaEntityVwcHistory(h))));
    }

    getSuggestedPercentAdjustments(orgId: number, turfRadCourseId: number = null): Observable<SaSuggestedPercentAdjust[]> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getSuggestedPercentAdjustmentsUrl(orgId, turfRadCourseId), HttpMethod.Get)
            .pipe(map((result: SaSuggestedPercentAdjust[]) => result.map(h => new SaSuggestedPercentAdjust(h))));
    }

    recalculateAdjustments(orgId: number, turfRadCourseId: number, settings: SpatialAdjustSettings): Observable<any> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getRecalculateAdjustmentsUrl(orgId, turfRadCourseId), HttpMethod.Put, settings)
            .pipe(map((result: SaSuggestedPercentAdjust[]) => result.map(h => new SaSuggestedPercentAdjust(h))));
    }

    updateTargetVwc(orgId: number, turfRadCourseId: number, stationId: string, targetVwcPercent: number, actualVwc: number, currentPercentAdjust: number): Observable<SaUpdateTargetVwcResponse> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.updateTargetVwcUrl, HttpMethod.Put, {
            orgId,
            turfRadCourseId,
            stationId,
            targetVwcPercent,
            actualVwc,
            currentPercentAdjust
        })
            .pipe(map(result => new SaUpdateTargetVwcResponse(result)));
    }

    bulkUpdateTargetVwc(orgId: number, turfRadCourseId: number, targetVwcPercent: number, adjustType: BulkAdjustType, correlationId?: string): Observable<any> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.bulkUpdateTargetVwcUrl, HttpMethod.Put, {
            orgId,
            turfRadCourseId,
            targetVwcPercent,
            updateAction: adjustType
        }, correlationId);
    }

    updateUserPercentAdjust(orgId: number, turfRadCourseId: number, stationId: string, userPercentAdjust: number): Observable<any> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.updateUserPercentAdjustUrl, HttpMethod.Put, {
            orgId,
            turfRadCourseId,
            stationId,
            userPercentAdjust
        });
    }

    updateAutoAdjust(orgId: number, turfRadCourseId: number, zoneId: string, zoneType: SaZoneType, isEnabled = false, isSelected = false): Observable<void> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.updateAutoAdjustUrl, HttpMethod.Put, {
            orgId,
            turfRadCourseId,
            zoneId,
            zoneType: zoneType,
            enabled: isEnabled,
            selected: isSelected
        });
    }

    setEntityEnabledState(zoneType: SaZoneType, zoneId: string, isEnabled: boolean, cascade: boolean = false): Observable<void> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getSetEntityEnabledStateUrl(zoneType, zoneId), HttpMethod.Put, {
            enabled: isEnabled,
            cascadeToChildren: cascade,
        });
    }

    updateChartSelection(updateDto: SaUpdateZoneChartSelection): Observable<any> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.updateChartSelectionUrl, HttpMethod.Put, updateDto);
    }

    toggleRowSelection(orgId: number, turfRadCourseId: number, zoneId: string, zoneType: SaZoneType, isRowSelected): Observable<any> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.toggleRowSelectionUrl, HttpMethod.Put, {
            orgId,
            turfRadCourseId,
            zoneId,
            zoneType,
            isRowSelected
        });
    }

    updateLastPushDate(stationIds: string[]): Observable<SaUpdateTargetVwcResponse> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.updateLastPushDateUrl, HttpMethod.Put, stationIds);
    }

    getStationsPushedTodayCount(orgId: number, turfRadCourseId: number = null): Observable<number> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getStationsPushedTodayCountUrl(orgId, turfRadCourseId), HttpMethod.Get);
    }

    updateCourseName(saCourse: SpatialAdjustCourse): Observable<void> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getUpdateCourseNameUrl(saCourse.turfRadCourseId), HttpMethod.Put, saCourse);
    }

    getSelectedStationsCount(orgId: number, turfRadCourseId: number = null): Observable<number> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getStationsSelectedCountUrl(orgId, turfRadCourseId), HttpMethod.Get);
    }

    updateSoilFactors(updates: SoilFactorUpdate[]): Observable<SaSuggestedPercentAdjust[]> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.updateSoilFactorsUrl, HttpMethod.Put, updates)
            .pipe(map((result: SaSuggestedPercentAdjust[]) => result.map(h => new SaSuggestedPercentAdjust(h))));
    }

    // Request a refresh of the scan data from the server for the current user's site.
    requestScanDataRefresh(): Observable<any> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.requestScanDataRefreshUrl, HttpMethod.Post);
    }

    hasTurfRadAccess(orgId: number): Observable<boolean> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.hasTurfRadAccessUrl(orgId), HttpMethod.Get);
    }

    hasScanData(orgId: number, turfRadCourseId: number = null): Observable<boolean> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.hasScanDataUrl(orgId, turfRadCourseId), HttpMethod.Get);
    }

    getLastScanDate(orgId: number, turfRadCourseId: number = null): Observable<string> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getLastScanDateUrl(orgId, turfRadCourseId), HttpMethod.Get);
    }

    getTargetProfiles(orgId: number): Observable<SaTargetProfile[]> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getTargetProfilesUrl(orgId), HttpMethod.Get)
            .pipe(map((result: SaTargetProfile[]) => result.map(p => new SaTargetProfile(p))));
    }

    getAppliedTargetProfiles(orgId: number): Observable<AppliedSaTargetProfile[]> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.getAppliedTargetProfilesUrl(orgId), HttpMethod.Get)
            .pipe(map((result: AppliedSaTargetProfile[]) => result.map(p => new AppliedSaTargetProfile(p))));
    }

    upsertTargetProfiles(orgId: number, profiles: SaTargetProfile[]): Observable<SaQueuedOperation> {
        return this.apiService.apiRequest<any>(SpatialAdjustApiService.upsertTargetProfilesUrl(orgId), HttpMethod.Post, profiles)
            .pipe(map(result => new SaQueuedOperation(result)))
    }

    // =========================================================================================================================================================
    // URLs
    // =========================================================================================================================================================

    private static get teraRadbaseUrl(): string { return `${ApiService.baseApiUrl}TerraRad`; }

    private static get baseUrl(): string { return `${ApiService.baseApiUrl}SpatialAdjust`; }

    private static getSprinklerStatsUrl(orgId: number, fromDate: string, page?: number): string {
        let url = `${SpatialAdjustApiService.teraRadbaseUrl}/sprinklerStats?orgId=${orgId}&dateRangeAfter=${fromDate}`;
        if (page != null) { url += `&page=${page}` }

        return url;
    }

    private static getCoursesUrl(orgId: number): string { return `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/courses`; }

    private static getDataUrl(orgId: number, turfRadCourseId: number = null): string {
        let url = `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/areas`;
        if (turfRadCourseId != null) { url += `?turfRadCourseId=${turfRadCourseId}`; }

        return url;
    }

    private static getTodayEtUrl(orgId: number): string { return `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/et`; }

    private static get getVwcHistoryUrl(): string { return `${SpatialAdjustApiService.baseUrl}/entities/vwc/filter`; }

    private static getSuggestedPercentAdjustmentsUrl(orgId: number, turfRadCourseId: number = null): string {
        let url = `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/stations/suggestedPercentAdjust`;
        if (turfRadCourseId != null) { url += `?turfRadCourseId=${turfRadCourseId}`; }

        return url;
    }

    private static getRecalculateAdjustmentsUrl(orgId: number, turfRadCourseId: number = null): string {
        let url = `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/adjustments/recalculate`;
        if (turfRadCourseId != null) { url += `?turfRadCourseId=${turfRadCourseId}`; }

        return url;
    }

    private static get updateTargetVwcUrl(): string { return `${SpatialAdjustApiService.baseUrl}/stations/targetVwc`; }

    private static get bulkUpdateTargetVwcUrl(): string { return `${SpatialAdjustApiService.baseUrl}/stations/targetVwc/bulkUpdate`; }

    private static get updateUserPercentAdjustUrl(): string { return `${SpatialAdjustApiService.baseUrl}/stations/userPercentAdjust`; }

    private static get updateAutoAdjustUrl(): string { return `${SpatialAdjustApiService.baseUrl}/zones/autoAdjust`; }

    private static getSetEntityEnabledStateUrl(zoneType: SaZoneType, entityId: string): string {
        return `${SpatialAdjustApiService.baseUrl}/zones/${zoneType}/${entityId}/enabled`;
    }

    private static get updateChartSelectionUrl(): string { return `${SpatialAdjustApiService.baseUrl}/zones/chartSelection`; }

    private static get toggleRowSelectionUrl(): string { return `${SpatialAdjustApiService.baseUrl}/zones/rowSelection`; }

    private static get updateLastPushDateUrl(): string { return `${SpatialAdjustApiService.baseUrl}/stations/lastPushDate`; }

    private static getUpdateCourseNameUrl(turfRadCourseId: number = null): string {
        return `${SpatialAdjustApiService.baseUrl}/courses/${turfRadCourseId}/name`;
    }

    private static getStationsPushedTodayCountUrl(orgId: number, turfRadCourseId: number = null): string {
        let url = `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/stations/pushedTodayCount`;
        if (turfRadCourseId != null) { url += `?turfRadCourseId=${turfRadCourseId}`; }

        return url;
    }

    private static getStationsSelectedCountUrl(orgId: number, turfRadCourseId: number = null): string {
        let url = `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/stations/selectedCount`;
        if (turfRadCourseId != null) { url += `?turfRadCourseId=${turfRadCourseId}`; }

        return url;
    }

    private static get updateSoilFactorsUrl(): string { return `${SpatialAdjustApiService.baseUrl}/stations/soilFactors`; }

    private static get requestScanDataRefreshUrl(): string { return `${SpatialAdjustApiService.baseUrl}/scanData/refresh`; }

    private static hasTurfRadAccessUrl(orgId: number): string {
        return `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/hasAccess`;
    }

    private static hasScanDataUrl(orgId: number, turfRadCourseId: number = null) {
        let url = `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/hasScanData`;
        if (turfRadCourseId != null) { url += `?turfRadCourseId=${turfRadCourseId}`; }

        return url;
    }

    private static getLastScanDateUrl(orgId: number, turfRadCourseId: number = null) {
        let url = `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/lastScanDate`;
        if (turfRadCourseId != null) { url += `?turfRadCourseId=${turfRadCourseId}`; }

        return url;
    }

    private static getTargetProfilesUrl(orgId: number): string {
        return `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/targetProfiles`;
    }

    private static getAppliedTargetProfilesUrl(orgId: number): string {
        return `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/targetProfiles/applied`;
    }

    private static upsertTargetProfilesUrl(orgId: number): string {
        return `${SpatialAdjustApiService.baseUrl}/organizations/${orgId}/targetProfiles`;
    }
}
